import Typography from "@atoms/typography/Typography";
import { gridStyles } from "@layout/grid";
import { cn, tw } from "@utils/tailwind";
import { X as XIcon } from "lucide-react";
import { FC, useEffect, useRef } from "react";

const OOPS_MSG = "Oops. Something went wrong. Our team is working on it...";

const inputStyles = tw`flex
  h-9
  w-full
  bg-transparent
  py-2
  text-base
  font-medium
  outline-none
  transition-opacity
  placeholder:text-grey-faded
  disabled:cursor-not-allowed
  disabled:opacity-50
  md:h-10
  md:py-2.5
  xl:py-1.5
  xl:text-lg
  xl:tracking-[-0.01em]`;

export type SearchBarProps = {
  value: string;
  isError: boolean;
  onChange: (value: string) => void;
  className?: string;
};

export const SearchBar: FC<SearchBarProps> = ({
  value,
  isError,
  onChange,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const clearAll = () => {
    onChange("");
    inputRef.current?.focus();
  };

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  return (
    <div className={cn(gridStyles.item, "flex flex-col gap-2", className)}>
      <div
        className={cn(
          "flex items-center border-b border-current text-content-primary-on-light",
          isError && "text-error"
        )}
      >
        <input
          type="text"
          placeholder="What are you looking for?"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          aria-autocomplete="list"
          role="search"
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") clearAll();
          }}
          className={inputStyles}
        />
        <button
          tabIndex={value ? 0 : -1}
          className={cn(
            "shrink-0 transition-[margin,opacity,width] ease-in-out",
            value
              ? "mx-2.5 w-5 opacity-100 lg:hover:opacity-60 lg:active:opacity-60"
              : "pointer-events-none mx-0 w-0 opacity-0"
          )}
          onClick={clearAll}
        >
          <XIcon className="size-5 fill-current" />
        </button>
      </div>
      <Typography
        tag="p"
        variant="sm"
        className={cn(
          gridStyles.itemShrinked,
          !isError && "hidden",
          "text-error"
        )}
      >
        {OOPS_MSG}
      </Typography>
    </div>
  );
};
